// How a volunteer's phone reads and dials. InLeague's `primaryPhone` arrives
// as whatever the registrar typed, most often ten bare digits.

/** Only digits, spaces and phone punctuation, with an optional leading `+`.
 *  Anything else (letters, so an extension or "N/A") is not reformatted or
 *  dialed: dropping the letters would dial a different number. */
const DIALABLE = /^\+?[\d\s().\-]+$/

/** Digits of a US number (10, or 11 with a leading 1), else null. A `+`
 *  prefix is US only as `+1` and eleven digits. */
function usDigits(raw: string): string | null {
  const s = raw.trim()
  if (!DIALABLE.test(s)) return null
  const d = s.replace(/\D/g, '')
  if (d.length === 11 && d.startsWith('1')) return d.slice(1)
  if (d.length === 10 && !s.startsWith('+')) return d
  return null
}

/** `(973) 896-9928` for a US number; anything else as stored, trimmed. */
export function formatPhone(raw: string): string {
  const d = usDigits(raw)
  return d ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : raw.trim()
}

/** A `tel:` href built from digits only: E.164 for a US number, otherwise the
 *  digits with any leading `+` kept. Null when the value is not safely
 *  dialable (no digits, or letters such as an extension), so the caller shows
 *  it as text. */
export function phoneHref(raw: string): string | null {
  const d = usDigits(raw)
  if (d) return `tel:+1${d}`
  const s = raw.trim()
  const digits = s.replace(/\D/g, '')
  if (!DIALABLE.test(s) || digits === '') return null
  return `tel:${s.startsWith('+') ? '+' : ''}${digits}`
}
